import { getContinent, getCountryName, getNocFromMapName } from './nocContinent';

export const entryKey = (entry) =>
  `${entry?.firstName || ''} ${entry?.lastName || ''}|${entry?.country || ''}`
    .trim()
    .toLowerCase();

const cutoffDistance = (rank, cutoff) => Math.abs((rank || cutoff) - cutoff);

const sortLineCrossers = (a, b, cutoff) => {
  const aDist = cutoffDistance(
    a.crossingType === 'out' ? a.startRank : a.endRank,
    cutoff
  );
  const bDist = cutoffDistance(
    b.crossingType === 'out' ? b.startRank : b.endRank,
    cutoff
  );
  return aDist - bDist;
};

export const buildLineCrossersFromSnapshots = (
  previousEntries = [],
  currentEntries = [],
  cutoff = 60
) => {
  if (!previousEntries.length || !currentEntries.length) return [];

  const previousByKey = new Map(previousEntries.map((entry) => [entryKey(entry), entry]));
  const currentByKey = new Map(currentEntries.map((entry) => [entryKey(entry), entry]));
  const crossers = [];

  currentEntries.forEach((entry) => {
    const key = entryKey(entry);
    if (previousByKey.has(key)) return;

    crossers.push({
      key,
      entry,
      crossingType: 'in',
      startRank: null,
      endRank: entry.olympicRank,
      delta: null,
      absDelta: cutoff - entry.olympicRank,
    });
  });

  previousEntries.forEach((entry) => {
    const key = entryKey(entry);
    if (currentByKey.has(key)) return;

    crossers.push({
      key,
      entry,
      crossingType: 'out',
      startRank: entry.olympicRank,
      endRank: null,
      delta: null,
      absDelta: entry.olympicRank - cutoff,
    });
  });

  return crossers.sort((a, b) => sortLineCrossers(a, b, cutoff));
};

export const formatLineCrosserMeta = (mover) => {
  if (mover.crossingType === 'in') {
    return `Moved in | Outside → Rank ${mover.endRank}`;
  }
  return `Moved out | Rank ${mover.startRank} → Outside the line`;
};

export const formatLineCrosserRankCopy = (mover) => {
  if (mover.crossingType === 'in') {
    return `Outside → Rank ${mover.endRank ?? '—'}`;
  }
  return `Rank ${mover.startRank ?? '—'} → Outside the line`;
};

export const matchesLineCrosserFilters = (mover, { term = '', continent = '' } = {}) => {
  if (!term && !continent) return true;

  const entry = mover.entry;
  if (!entry) return false;

  if (continent && getContinent(entry.country) !== continent) return false;

  if (!term) return true;

  const normalizedTerm = term.toLowerCase();
  const mappedNoc = getNocFromMapName(term).toLowerCase();
  const fullName = `${entry.firstName || ''} ${entry.lastName || ''}`.toLowerCase();
  const country = String(entry.country || '').toLowerCase();
  const countryName = getCountryName(entry.country).toLowerCase();

  return (
    fullName.includes(normalizedTerm) ||
    country.includes(normalizedTerm) ||
    countryName.includes(normalizedTerm) ||
    (mappedNoc && country === mappedNoc)
  );
};
